import { createServerFn } from "@tanstack/react-start";
import { getRequestIP } from "@tanstack/react-start/server";

export type AuthAction = "signin" | "signup" | "reset";

type Rule = {
  /** Window in minutes over which attempts are counted. */
  windowMinutes: number;
  /** Max failed attempts for one identifier (email) before lockout. */
  maxPerIdentifier: number;
  /** Max failed attempts from one network address before lockout. */
  maxPerIp: number;
  /** Whether successful attempts also count towards the limit. */
  countSuccess: boolean;
};

const RULES: Record<AuthAction, Rule> = {
  // Password guessing: only failures matter.
  signin: { windowMinutes: 15, maxPerIdentifier: 5, maxPerIp: 20, countSuccess: false },
  // Account farming: every attempt counts, success included.
  signup: { windowMinutes: 60, maxPerIdentifier: 3, maxPerIp: 10, countSuccess: true },
  // Reset-email flooding: every attempt counts.
  reset: { windowMinutes: 60, maxPerIdentifier: 3, maxPerIp: 10, countSuccess: true },
};

const ACTIONS = new Set<AuthAction>(["signin", "signup", "reset"]);

function parseAction(value: unknown): AuthAction {
  if (typeof value !== "string" || !ACTIONS.has(value as AuthAction)) {
    throw new Error("Invalid action");
  }
  return value as AuthAction;
}

function parseEmail(value: unknown): string {
  if (typeof value !== "string") throw new Error("Invalid email");
  const email = value.trim().toLowerCase();
  if (email.length < 3 || email.length > 255) throw new Error("Invalid email");
  return email;
}

/**
 * Emails are never stored in the attempts log — only a stable digest, so the
 * table can never leak an address list even to server-side readers.
 */
async function digest(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(`finendra:auth-attempt:${value}`);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (b) => b.toString(16).padStart(2, "0")).join("");
}

function callerIp(): string | null {
  try {
    return getRequestIP({ xForwardedFor: true }) ?? null;
  } catch {
    return null;
  }
}

async function evaluate(action: AuthAction, email: string) {
  const rule = RULES[action];
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const since = new Date(Date.now() - rule.windowMinutes * 60_000).toISOString();
  const identifier = await digest(email);
  const ip = callerIp();

  let query = supabaseAdmin
    .from("auth_attempts")
    .select("identifier, ip, success, created_at")
    .eq("action", action)
    .gte("created_at", since)
    .order("created_at", { ascending: false })
    .limit(200);

  // Fetch anything matching either bucket, then count locally.
  query = ip
    ? query.or(`identifier.eq.${identifier},ip.eq.${ip}`)
    : query.eq("identifier", identifier);

  const { data: rows } = await query;
  const relevant = (rows ?? []).filter((r) => rule.countSuccess || !r.success);

  const forIdentifier = relevant.filter((r) => r.identifier === identifier);
  const forIp = ip ? relevant.filter((r) => r.ip === ip) : [];

  const overIdentifier = forIdentifier.length >= rule.maxPerIdentifier;
  const overIp = forIp.length >= rule.maxPerIp;

  if (!overIdentifier && !overIp) {
    return {
      allowed: true as const,
      retryAfter: 0,
      remaining: Math.max(0, rule.maxPerIdentifier - forIdentifier.length),
    };
  }

  // Lock until the oldest counted attempt in the offending bucket ages out.
  const bucket = overIdentifier ? forIdentifier : forIp;
  const oldest = bucket[bucket.length - 1];
  const unlockAt = oldest
    ? new Date(oldest.created_at).getTime() + rule.windowMinutes * 60_000
    : Date.now() + rule.windowMinutes * 60_000;

  return {
    allowed: false as const,
    retryAfter: Math.max(1, Math.ceil((unlockAt - Date.now()) / 1000)),
    remaining: 0,
  };
}

/** Called before hitting the auth service. Returns whether the attempt may proceed. */
export const checkAuthThrottle = createServerFn({ method: "POST" })
  .inputValidator((input: { action: AuthAction; email: string }) => ({
    action: parseAction(input?.action),
    email: parseEmail(input?.email),
  }))
  .handler(async ({ data }) => evaluate(data.action, data.email));

/**
 * Called after the auth service responds. `success` is only used to decide
 * whether the attempt counts towards signup/reset volume limits — it never
 * clears an existing sign-in lockout, so a forged value cannot unlock anyone.
 */
export const recordAuthAttempt = createServerFn({ method: "POST" })
  .inputValidator((input: { action: AuthAction; email: string; success?: boolean }) => ({
    action: parseAction(input?.action),
    email: parseEmail(input?.email),
    success: input?.success === true,
  }))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    await supabaseAdmin.from("auth_attempts").insert({
      action: data.action,
      identifier: await digest(data.email),
      ip: callerIp(),
      success: data.success,
    });

    // Opportunistic cleanup so the log never grows unbounded.
    if (Math.random() < 0.02) {
      await supabaseAdmin
        .from("auth_attempts")
        .delete()
        .lt("created_at", new Date(Date.now() - 24 * 60 * 60_000).toISOString());
    }

    return { ok: true };
  });
